import React from "react";
import { useLatest } from "../hooks";
import AutocompleteSelect from "./AutocompleteSelect";
import * as api from "../services/api";
import { selectKeys } from "shared/src/util.mjs";

function useProductFilter({ client_id, company_id }) {
  return React.useMemo(() => {
    if (client_id == null || company_id == null) {
      return null;
    }

    return {
      company_id: { eq: company_id },
      client_id: { eq: client_id },
    };
  }, [client_id, company_id]);
}

async function fetchProductOptions(signal, filter) {
  if (filter == null) {
    return [];
  }

  const res = await api.productOptions(
    {
      filter: filter,
    },
    { signal },
  );

  return res.data.products.data;
}

function useProductOptions(filter) {
  const [options, setOptions] = React.useState();
  const result = React.useMemo(
    () => ({
      options: options ?? [],
      loading: options == null,
    }),
    [options],
  );

  useLatest(
    async (signal) => {
      if (filter == null) {
        setOptions([]);
        return;
      }

      setOptions(null);
      setOptions(await fetchProductOptions(signal, filter));
    },
    [filter],
  );

  return result;
}

/**
 * @typedef {Pick<import('./AutocompleteSelect').AutocompleteSelectProps, 'required' | 'onChange'>} _AutocompleteSelectProps
 *
 * @typedef _ProductSelectorProps
 * @property {string} props.company_id
 * @property {string} props.client_id
 * @property {string} [props.default_product_id]
 *
 * @typedef {_AutocompleteSelectProps & _ProductSelectorProps} ProductSelectorProps
 *
 * @param {ProductSelectorProps} props
 */
export function ProductSelector(props) {
  const filter = useProductFilter(props);
  const options = useProductOptions(filter);

  const autocompleteSelectProps = selectKeys(props, ["onChange", "required"]);

  const defaultValue =
    props.default_product_id == null
      ? null
      : options.options.find((o) => o.id === props.default_product_id);

  return (
    <AutocompleteSelect
      key={options.loading ? "0" : "1"}
      loading={options.loading}
      options={options.options}
      defaultValue={defaultValue}
      label="Produkt"
      {...autocompleteSelectProps}
    />
  );
}
